import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useActiveBase } from "@/context/BaseContext";
import { Button } from "@/components/ui/button";
import {
  TruckIcon, WarehouseIcon, GaugeIcon, PackageIcon, BoxesIcon, WrenchIcon,
  GaugeCircleIcon, LogOutIcon, ShieldCheckIcon, HistoryIcon, DatabaseIcon, ShieldIcon,
  BarChart3Icon, BookOpenIcon, EyeIcon, XIcon, DoorOpenIcon, CalendarClockIcon, ContainerIcon, SatelliteIcon, ScaleIcon,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: GaugeCircleIcon, roles: ["manager", "admin"], gestao: true },
  { to: "/rastreamento", label: "Rastreamento GPS", icon: SatelliteIcon, roles: ["operator", "manager", "admin"], module: "rastreamento" },
  { to: "/portaria", label: "Portaria", icon: DoorOpenIcon, roles: ["operator", "manager", "admin"], module: "portaria" },
  { to: "/balanca", label: "Balança", icon: ScaleIcon, roles: ["operator", "manager", "admin"], module: "balanca", optIn: true },
  { to: "/logistica", label: "Agendamento Doca", icon: CalendarClockIcon, roles: ["operator", "manager", "admin"], module: "docas", fornecedorModule: true },
  { to: "/logistica/operacao", label: "Operação Logística", icon: ContainerIcon, roles: ["operator", "manager", "admin"], module: "docas", fornecedorModule: true },
  { to: "/recebimento", label: "Recebimento", icon: TruckIcon, roles: ["operator", "manager", "admin"], module: "envase" },
  { to: "/triagem", label: "Contentores Vazios", icon: WarehouseIcon, roles: ["operator", "manager", "admin"], module: "triagem" },
  { to: "/manutencao", label: "Manutenção", icon: WrenchIcon, roles: ["operator", "manager", "admin"], module: "triagem" },
  { to: "/envase", label: "Linha de Envase", icon: GaugeIcon, roles: ["operator", "manager", "admin"], module: "envase" },
  { to: "/estoque", label: "Estoque Cheios", icon: BoxesIcon, roles: ["operator", "manager", "admin"], module: "armazenagem" },
  { to: "/expedicao", label: "Expedição", icon: PackageIcon, roles: ["operator", "manager", "admin"], module: "armazenagem" },
  { to: "/relatorios", label: "Relatórios BI", icon: BarChart3Icon, roles: ["manager", "admin"], gestao: true },
  { to: "/historico", label: "Histórico Ativos", icon: HistoryIcon, roles: ["manager", "admin"], gestao: true },
  { to: "/banco-dados", label: "Banco de Dados", icon: DatabaseIcon, roles: ["manager", "admin"], gestao: true },
  { to: "/manual", label: "Manual", icon: BookOpenIcon, roles: ["admin"] },
  { to: "/admin", label: "Painel ADM", icon: ShieldIcon, roles: ["admin", "manager"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { activeBase, clearActiveBase } = useActiveBase();
  const navigate = useNavigate();
  const location = useLocation();
  const [myBase, setMyBase] = React.useState(null);
  const [modFornecedores, setModFornecedores] = React.useState(true);

  React.useEffect(() => {
    if (!user) return;
    import("@/lib/api").then(({ api }) => {
      api.get("/logistica/settings").then(({ data }) => setModFornecedores(data.mod_fornecedores !== false)).catch(() => {});
    });
    // Busca módulos da base do usuário (para esconder itens de nav desligados)
    if (user.role === "admin" || !user.base) return;
    import("@/lib/api").then(({ api }) => {
      api.get("/bases").then(({ data }) => {
        setMyBase(data.find((b) => b.id === user.base || b.code === user.base) || null);
      }).catch(() => {});
    });
  }, [user]);

  // Telas gerais de gestão (Dashboard, BI, Histórico, Banco de Dados) só fazem sentido se a base opera o WMS
  // (envase/triagem/armazenagem/docas/GPS). Base só com Portaria/Balança não as exibe.
  const WMS_MODULES = ["envase", "triagem", "armazenagem", "docas", "rastreamento"];
  const baseTemWms = user?.role === "admin" || !myBase || WMS_MODULES.some((k) => myBase.modules?.[k] !== false);
  const allowed = NAV.filter((n) => {
    if (!n.roles.includes(user?.role)) return false;
    if (n.gestao && !baseTemWms) return false;
    // Toggle por base: esconde módulo explicitamente desligado (admin sempre vê)
    if (n.module && user?.role !== "admin" && myBase?.modules?.[n.module] === false) return false;
    // Módulos opt-in (ex.: Balança): só aparecem quando o admin LIGOU explicitamente na base
    if (n.optIn && user?.role !== "admin" && myBase?.modules?.[n.module] !== true) return false;
    // Restrição por USUÁRIO: se o admin/gestor marcou onde a pessoa atua, só esses módulos aparecem
    if (n.module && user?.role !== "admin" && user?.modules?.length && !user.modules.includes(n.module)) return false;
    // Gestor com módulos marcados: telas gerais de gestão (Dashboard, BI, Histórico, Banco de Dados) também somem — só vê o que foi marcado
    if (n.gestao && user?.role !== "admin" && user?.modules?.length) return false;
    // Toggle global do módulo Fornecedores/Logística
    if (n.fornecedorModule && user?.role !== "admin" && !modFornecedores) return false;
    return true;
  });

  React.useEffect(() => {
    if (activeBase && user?.role !== "admin") {
      clearActiveBase();
    }
  }, [user, activeBase, clearActiveBase]);
  const showImpersonation = !!activeBase && user?.role === "admin";
  const rotaGestao = ["/dashboard", "/relatorios", "/historico", "/banco-dados"].includes("/" + location.pathname.split("/")[1]);
  React.useEffect(() => {
    if (myBase && !baseTemWms && rotaGestao) {
      const primeiro = allowed.find((n) => n.to !== "/admin");
      navigate(primeiro ? primeiro.to : "/admin", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBase, baseTemWms, rotaGestao]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row">
      <aside className="md:w-60 md:min-h-screen border-b md:border-b-0 md:border-r border-border bg-[#0d0d10] flex md:flex-col">
        <div className="p-4 border-b border-border md:border-b md:border-r-0 flex items-center gap-3 flex-1 md:flex-none">
          <div className="w-10 h-10 bg-[#FFD60A] flex items-center justify-center" data-testid="brand-logo">
            <ShieldCheckIcon className="w-6 h-6 text-black" strokeWidth={2.5} />
          </div>
          <div className="hidden md:block">
            <div className="font-display font-black text-lg leading-none">CHEM·WMS</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Envase & Telemetria</div>
          </div>
        </div>

        <nav className="hidden md:flex md:flex-col md:flex-1 p-2 gap-1">
          {allowed.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 border border-transparent hover:border-[#FFD60A]/40 text-sm ${
                  isActive ? "bg-[#FFD60A] text-black font-bold" : "text-muted-foreground hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="uppercase text-[11px] tracking-widest">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block p-4 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Usuário</div>
          <div className="text-sm text-white font-semibold" data-testid="user-name">{user?.name}</div>
          <div className="text-[10px] uppercase text-[#00E5FF] font-mono">{user?.role}</div>
          <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn" className="mt-3 w-full rounded-none border-border hover:border-[#FF3B30] hover:text-[#FF3B30]">
            <LogOutIcon className="w-3 h-3 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen">
        {showImpersonation && (
          <div className="sticky top-0 z-40 bg-gradient-to-r from-[#FFD60A] via-[#FFB800] to-[#FF9F0A] text-black px-4 py-2 flex items-center gap-3 flex-wrap border-b-2 border-black shadow-lg" data-testid="impersonation-banner">
            <EyeIcon className="w-4 h-4" strokeWidth={2.5} />
            <span className="text-xs uppercase tracking-widest font-bold">Modo Admin · Visualizando operação da:</span>
            <span className="font-display font-black text-base uppercase" data-testid="impersonation-base-name">{activeBase.name}</span>
            <span className="font-mono text-[10px] bg-black/20 px-2 py-0.5 rounded">CÓD {activeBase.code}</span>
            <button
              onClick={() => { clearActiveBase(); navigate("/admin"); }}
              data-testid="btn-sair-modo-filial"
              className="ml-auto bg-black text-[#FFD60A] font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-black/85 flex items-center gap-1"
            >
              <XIcon className="w-3 h-3" /> Sair do Modo Filial
            </button>
          </div>
        )}
        <div className="md:hidden flex items-center justify-between px-4 py-2 border-b border-border bg-[#0d0d10]">
          <div className="text-xs text-muted-foreground">
            <span data-testid="m-user-name" className="text-white font-semibold">{user?.name}</span>
            <span className="text-[#00E5FF] ml-2 uppercase">{user?.role}</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="m-logout-btn">
            <LogOutIcon className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </main>
    </div>
  );
}
